import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Requires authentication for access
 *
 * Embedded mode (?embedded=true) bypasses authentication for landing page iframe integration.
 * This allows the demo preview to navigate between wireframe pages without requiring login.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  // Allow embedded mode to bypass authentication (for landing page iframe)
  const isEmbeddedMode = searchParams.get('embedded') === 'true';

  if (isEmbeddedMode) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin border-4 border-solid border-accent-500 border-r-transparent mb-4"></div>
          <p className="text-ink-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
